import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

/**
 * Theme configuration shared across the app.
 *
 * Previously lived in charts/logic/BaseVisualization.ts (Highcharts era).
 * Now defined here as the single source of truth.
 */
export interface ThemeConfig {
  mode: 'light' | 'dark';
  colors: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    gridLines: string;
    chartBackground: string;
    appBackground: string;
  };
}

export const lightTheme: ThemeConfig = {
  mode: 'light',
  colors: {
    primary: '#f8f9fa',
    secondary: '#2c3e50',
    background: 'rgba(255, 255, 255, 0.95)',
    text: '#333333',
    gridLines: '#e0e0e0',
    chartBackground: '#ffffff',
    appBackground: '#f8f9fa',
  },
};

export const darkTheme: ThemeConfig = {
  mode: 'dark',
  colors: {
    primary: '#5dade2',
    secondary: '#ffffff',
    background: '#292929',
    text: '#ffffff',
    gridLines: '#404040',
    chartBackground: '#292929',
    appBackground: '#141414',
  },
};

interface ThemeContextType {
  theme: ThemeConfig;
  toggleTheme: () => void;
  isDarkMode: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);
ThemeContext.displayName = 'ThemeContext';

const THEME_STORAGE_KEY = 'dashboard-theme-mode';

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeConfig>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'dark') return darkTheme;
    if (saved === 'light') return lightTheme;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? darkTheme : lightTheme;
  });

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme.mode);
  }, [theme.mode]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--theme-background', theme.colors.background);
    root.style.setProperty('--theme-text', theme.colors.text);
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-secondary', theme.colors.secondary);
    root.style.setProperty('--theme-grid-lines', theme.colors.gridLines);
    root.style.setProperty('--theme-chart-background', theme.colors.chartBackground);
    root.style.setProperty('--theme-app-background', theme.colors.appBackground);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setThemeState(current => (current.mode === 'light' ? darkTheme : lightTheme));
  }, []);

  const isDarkMode = theme.mode === 'dark';

  const value = useMemo<ThemeContextType>(
    () => ({ theme, toggleTheme, isDarkMode }),
    [theme, toggleTheme, isDarkMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};